import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { BarChart3 } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

const CITY_BENCHMARKS = [
  { city: "New York City", ratio: 28 },
  { city: "San Francisco", ratio: 30 },
  { city: "Austin", ratio: 18 },
  { city: "Cleveland", ratio: 10 },
  { city: "London", ratio: 25 },
  { city: "Amsterdam", ratio: 22 },
  { city: "Berlin", ratio: 24 },
]

const getInterpretation = (ratio: number) => {
  if (ratio < 15) return { label: "Buy-Favorable", cls: "text-green-600 bg-green-50 border-green-200", desc: "Buying tends to be cheaper than renting in this market." }
  if (ratio < 20) return { label: "Neutral", cls: "text-amber-600 bg-amber-50 border-amber-200", desc: "Buying and renting are roughly comparable in cost." }
  if (ratio < 25) return { label: "Rent-Favorable", cls: "text-blue-600 bg-blue-50 border-blue-200", desc: "Renting tends to be more cost-effective in this market." }
  return { label: "Strong Renting Market", cls: "text-violet-600 bg-violet-50 border-violet-200", desc: "High prices relative to rents strongly favor renting over buying." }
}

export default function PriceToRentRatioCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [homePrice, setHomePrice] = useState("")
  const [rentInput, setRentInput] = useState("")
  const [rentType, setRentType] = useState<"monthly" | "annual">("monthly")

  const results = useMemo(() => {
    const price = parseFloat(homePrice)
    const rent = parseFloat(rentInput)
    if (!price || !rent || price === 0) return null

    const annualRent = rentType === "monthly" ? rent * 12 : rent
    const ratio = price / annualRent
    const rentalYield = (annualRent / price) * 100
    const interpretation = getInterpretation(ratio)

    return { ratio, annualRent, rentalYield, interpretation }
  }, [homePrice, rentInput, rentType])

  const fmt = (n: number) => `${currency}${Math.round(n).toLocaleString()}`

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Property Details</p>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Home Price / Market Value</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                  <input
                    type="number"
                    min="0"
                    value={homePrice}
                    onChange={e => setHomePrice(e.target.value)}
                    placeholder="e.g. 350000"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Rent Amount</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                    <input
                      type="number"
                      min="0"
                      value={rentInput}
                      onChange={e => setRentInput(e.target.value)}
                      placeholder={rentType === "monthly" ? "e.g. 1400" : "e.g. 16800"}
                      className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <select
                    value={rentType}
                    onChange={e => setRentType(e.target.value as "monthly" | "annual")}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                <p className="text-xs text-blue-700 leading-relaxed">
                  The Price-to-Rent ratio shows how many years of rent it would take to cover the home purchase price. Higher ratios favor renting; lower ratios favor buying.
                </p>
              </div>
            </div>

            {/* Result Panel */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter a home price and rent to see the ratio</p>
              ) : (
                <>
                  <div className="text-center py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Price-to-Rent Ratio</p>
                    <p className="text-6xl font-extrabold text-primary">{results.ratio.toFixed(1)}</p>
                    <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold border ${results.interpretation.cls}`}>
                      {results.interpretation.label}
                    </span>
                    <p className="text-xs text-slate-500 mt-2">{results.interpretation.desc}</p>
                  </div>

                  <div className="border-t border-primary/10 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Annual Rent</span>
                      <span className="font-semibold text-slate-900">{fmt(results.annualRent)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Rental Yield (inverse)</span>
                      <span className="font-semibold text-slate-900">{results.rentalYield.toFixed(2)}%</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      A ratio of <strong>{results.ratio.toFixed(0)}</strong> means it takes {results.ratio.toFixed(0)} years of rent to cover the purchase price.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* City Benchmarks */}
          <div className="mt-6 border-t border-slate-200 pt-6">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">City Benchmarks (Typical P/R Ratios)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {CITY_BENCHMARKS.map(({ city, ratio }) => {
                const interp = getInterpretation(ratio)
                return (
                  <div key={city} className="rounded-xl border border-slate-200 bg-white p-3 text-center">
                    <p className="text-sm font-bold text-slate-900">{ratio}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-tight">{city}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${interp.cls}`}>
                      {interp.label.split(" ")[0]}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Price-to-Rent Ratio = Home Price ÷ Annual Rent\nRental Yield = Annual Rent ÷ Home Price × 100  (inverse)\n\nInterpretation:\n  < 15 → Buy-Favorable\n  15–20 → Neutral\n  20–25 → Rent-Favorable\n  > 25 → Strong Renting Market`}
        description="The ratio is essentially the inverse of rental yield. A ratio of 20 means a rental yield of 5%. The two metrics tell the same story from different perspectives."
      />

      <ToolFAQ
        items={[
          {
            q: "What is a good price-to-rent ratio?",
            a: "A ratio below 15 generally favors buying — your purchase cost is low relative to rent, suggesting good value. Ratios of 15–20 are neutral territory. Above 20 suggests renting is more cost-efficient. Above 25 (common in high-cost cities like London or San Francisco) strongly favors renting.",
          },
          {
            q: "How does price-to-rent ratio relate to rental yield?",
            a: "They are mathematical inverses. A P/R ratio of 20 equals a rental yield of 5% (1/20 = 5%). A ratio of 25 equals a 4% yield. Higher ratios correspond to lower yields — both suggest the property is expensive relative to the rent it generates.",
          },
          {
            q: "Should I use price-to-rent ratio to decide whether to buy?",
            a: "It's a useful starting point but not the complete picture. You must also factor in mortgage rates, total cost of ownership, potential appreciation, tax benefits, and personal stability needs. Use this ratio to gauge the market, then do a full buy vs rent analysis before deciding.",
          },
        ]}
      />

      <CTABox
        icon={BarChart3}
        heading="Ready to analyze your local market?"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
